/* eslint-disable sort-keys */
/* eslint-disable max-lines */

import {
    NinchatCannedMessage, Sso,
} from 'api/types/properties'
import {
    NinchatProperties, NinchatState,
} from 'api/types'
import React, {
    useContext,
    useEffect,
    useMemo,
} from 'react'

import {
    SendFeatures,
    SendRefType,
} from '../Send/types'

import {
    VideoCameraIcon, XIcon,
} from '../Icon'

import {
    jitsiMeet,
    jitsiToggleChat,
} from 'actions/jitsi'

import {
    keyframes, styled,
} from 'stitches/config'

import {
    useAppSelector,
    useAppSelectorWithShallowEquality,
} from '../../hooks'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import AudienceMetadata from '../AudienceMetadata'
import {AudienceMetadataContent} from 'components/AudienceMetadata/types'
import Button from '../Button'
import ChannelSidebar from 'components/ChannelSidebar'
import {ChatViewProps} from './types'
import {DEFAULT_HEADER_HEIGHT} from 'components/Embed/components/EmbedHeader/EmbedHeader'
import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import Highlight from '../Highlight'
import {JitsiState} from '../../reducers/jitsi'
import Menubar from '../Menubar'
import Messages from '../Messages'
import MetadataPromiseModal from 'components/Modal/MetadataPromiseModal'
import ScrollToBottom from '../ScrollToBottom'
import {ScrollToBottomProps} from '../ScrollToBottom/types'
import Send from '../Send'
import {config} from 'embed/listeners'
import {updateShowChannelSidebar} from '../../state/ui'
import useLightbox from 'hooks/lightbox'
import {usePromiseModal} from 'components/Modal/PromiseModal/hooks/usePromiseModal'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const CHAT_ON_VIDEO_DEFAULT_MAX_HEIGHT = '350px'
export const CHAT_ON_VIDEO_MAX_HEIGHT = `min(100vh, calc(${CHAT_ON_VIDEO_DEFAULT_MAX_HEIGHT} - $$containerHeaderHeight))`
export const CHAT_TOP_ON_VIDEO = `min(30%, calc(100% - ${CHAT_ON_VIDEO_MAX_HEIGHT}))`

type TopicType = string | null;

const StyledChat = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
})

const StyledChannel = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
})

const StyledChannelContent = styled('div', {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'flex-end',
    overflowY: 'auto',
})


const StyledVideoJoinDialogueContainer = styled('div', {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
})

const StyledVideoJoinDialogue = styled('div', {
    '& h3': {
        fontSize: '1.15rem',
        margin: '0 0 1.5rem 0',
    },
    '& svg': {
        color: '$icon',
        width: '3.5rem',
    },
    'alignItems': 'center',
    'display': 'flex',
    'flexDirection': 'column',
    'maxWidth': '400px',
    'textAlign': 'center',
})

const topAnimation = keyframes({
    from: {top: '100%'},
    to: {top: CHAT_TOP_ON_VIDEO},
})

const topAnimationReverse = keyframes({
    from: {top: CHAT_TOP_ON_VIDEO},
    to: {top: '100%'},
})


const StyledTopic = styled('div', {marginTop: '1.5rem'})

const StyledChannelMain = styled('div', {
    compoundVariants: [
        {
            activeVideoChannel: true,
            channelSidebarOpen: false,
            chatOpen: true,
            css: {width: '$chatWidth'},
        },
        {
            activeVideoChannel: true,
            channelSidebarOpen: true,
            chatOpen: false,
            css: {width: 0},
        },
        {
            activeVideoChannel: false,
            channelSidebarOpen: false,
            css: {width: '$chatWidth'},
            videoChannel: true,
        },
        {
            activeVideoChannel: false,
            channelSidebarOpen: true,
            css: {width: 0},
            videoChannel: true,
        },
        {
            isEmbedQueueChannel: true,
            activeVideoChannel: true,
            css: {
                '&&': {
                    position: 'fixed',
                    top: '100%',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    transition: 'none',
                    zIndex: '$chatOnJitsi',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                },
            },
        },
        {
            isEmbedQueueChannel: true,
            chatOpen: true,
            activeVideoChannel: true,
            css: {
                '&&': {
                    top: `${CHAT_TOP_ON_VIDEO}`,
                    animation: `${topAnimation} 0.3s ease-in-out`,
                },
            },
        },
        {
            isEmbedQueueChannel: true,
            chatOpen: false,
            firstChatOpening: true,
            activeVideoChannel: true,
            css: {'&&': {animation: `${topAnimationReverse} 0.3s ease-in-out`}},
        },
    ],
    display: 'flex',
    overflowX: 'auto',
    transition: 'width 0.2s; width: 0',
    variants: {
        activeVideoChannel: {true: {}},
        channelSidebarOpen: {true: {width: 0}},
        chatOpen: {false: {}},
        firstChatOpening: {false: {}},
        videoChannel: {false: {flexGrow: 1}},
        isEmbedQueueChannel: {true: {}},
    },
})

const StyledChannelMainContent = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    variants: {
        videoChannel: {
            true: {
                flexShrink: 0,
                width: '$chatWidth',
            },
        },
        isEmbedQueueChannel: {true: {width: '100%'}},
    },
})

const StyledBacklogContainer = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    position: 'relative',
    variants: {
        isEmbedQueueChannel: {
            true: {
                background: '$chatBacklogBackground',
                backgroundSize: '$chatBacklog',
            },
        },
        isVideoChannel: {true: {paddingTop: '10px'}},
        heroFullVisible: {true: {paddingTop: '10px'}},
    },

})

const StyledCommandBarContainer = styled('div', {
    backgroundColor: '$chatCommandBarWrapper',
    paddingTop: '10px',
})

const StyledBacklog = styled('div', {
    flex: '1 1 auto',
    height: '100px',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: '1rem 5%',
    variants: {
        isEmbedQueueChannel: {
            true: {
                'height': '10px', // smaller default height than in agents UI
                '> div': {minHeight: '15px'}, // nested div need to have bigger height to be able to show scroll bar, hero hiding logic is based on hiding scrollbar
            },
        },
    },
})

const StyledChatOnVideoHeader = styled('div', {
    'borderBottom': '1px solid $embedChatHeaderOnVideoBorder',
    'alignItems': 'center',
    'display': 'flex',
    'justifyContent': 'space-between',
    'padding': '0 10px 10px',
    '> div': {fontWeight: 700},
})

export const StyledCloseChatButtonContainer = styled('div', {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
    button: {padding: '$button'},
})

// eslint-disable-next-line complexity
const ChatView = (props: ChatViewProps): React.ReactElement => {
    const {
        cannedMessages,
        supportedCannedMessagesLanguages,
        chat,
        isAudienceChannel,
        isClosed,
        isSuspended,
        isVideoChannel,
        videoAutoJoin,
        realm_id,
        queue_id,
        notes,
    } = props

    const sendContainerRef = React.useRef<HTMLDivElement>(null)

    const targetOrigin = config.get('targetOrigin') || ''
    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'
    const heroFullVisible = embed?.embedState?.heroFullVisible
    const isContainerWithHeader = !embed?.floating && embed?.containerOptions?.showHeader

    const {close: closeLightbox} = useLightbox()

    const sendToParent = useSendToParent()

    const {
        activeVideoChannelId,
        isChatOpen,
        isJitsiActive,
        pendingAutoJoinChannelId,
    } = useAppSelectorWithShallowEquality(({jitsi}: {jitsi: JitsiState}) => ({
        activeVideoChannelId: jitsi.activeVideoChannelId,
        isChatOpen: jitsi.isChatOpen ?? false,
        isJitsiActive: jitsi?.isActive ?? false,
        pendingAutoJoinChannelId: jitsi?.pendingAutoJoinChannelId ?? null,
    }))

    // get operator or moderator id
    const agentIds = useAppSelector(({ninchat}): Array<NinchatProperties.UserID> => {
        if (!isAudienceChannel) {
            return []
        }
        return Object.entries(ninchat.user_channels[chat.channel_id ?? '']?.channel_members ?? {})
            .filter(([_, value]) => value.member_attrs?.moderator || value.member_attrs?.operator)
            .map(([id]) => id)
    })

    const openMetadataModal = usePromiseModal(MetadataPromiseModal, {instanceId: 'metadata'})

    const getTopic = (ninchat: NinchatState): TopicType => {
        if (chat && chat.channel_id) {
            return ninchat.user_channels[chat.channel_id]?.channel_attrs?.topic || null
        }

        return null
    }

    const topic = useSelector(({ninchat}: {ninchat: NinchatState}) => getTopic(ninchat))

    const dispatch = useDispatch()
    const {t} = useTranslation()

    const messagesContainerRef = React.useRef<null | HTMLDivElement>(null)

    const sendRef = React.useRef<SendRefType>(null)
    const [firstChatOpening, setFirstChatOpening] = React.useState<boolean>(false)

    const [historyLoadStatus, setHistoryLoadStatus] = React.useState<boolean>()
    const [scrollToBottomDisplay, setScrollToBottomDisplay] = React.useState<ScrollToBottomProps>({
        display: 'hide',
        isHighlighting: false,
        onClick: () => null,
        type: 'noop',
    })
    const showChannelSidebar = useAppSelector(({ui}) => ui.show_channel_sidebar || false)
    const isActiveVideoChannel = activeVideoChannelId === chat.channel_id
    const onToggleChannelSidebar = (event?: React.MouseEvent<HTMLButtonElement>): void => {
        event?.preventDefault()
        dispatch(updateShowChannelSidebar(!showChannelSidebar))
    }

    const onOpenModal = (userId?: string, sso?: Sso, secure?: AudienceMetadataContent['secure']) => {
        openMetadataModal({
            agentIds,
            channelId: chat.channel_id,
            realmId: realm_id,
            secure,
            sso,
            userId,
        }).then(
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            () => {},
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            () => {},
        )
    }

    const handleCloseChatOnVideo = () => {
        dispatch(jitsiToggleChat())
    }

    const onJoinVideoMeeting = (): void => {
        dispatch(jitsiMeet(chat.channel_id as string))
    }


    useEffect(() => {
        const isPendingAutoJoin = videoAutoJoin === true && pendingAutoJoinChannelId && pendingAutoJoinChannelId === chat.channel_id
        const isChannelClosed = isClosed || isSuspended
        const eligible = !isJitsiActive && !isChannelClosed && isAudienceChannel && isVideoChannel && isPendingAutoJoin

        // Consume the request regardless, so it can't trigger later via channel switching.
        if (eligible && chat?.channel_id) {
            dispatch(jitsiMeet(chat.channel_id))
        }
    }, [
        pendingAutoJoinChannelId,
        chat?.channel_id,
        isAudienceChannel,
        isVideoChannel,
        videoAutoJoin,
        isClosed,
        isSuspended,
        isJitsiActive,
    ])

    const onSelectCannedMessage = (m: NinchatCannedMessage): void => {
        sendRef?.current?.selectCannedMessage(m)
    }
    const getFeatures = (): SendFeatures => ({
        cannedMessages: isAudienceChannel,
        notes: notes.length > 0,
        tags: isAudienceChannel,
    })

    const features = useMemo(() => getFeatures(), [isAudienceChannel])

    useEffect(() => {
        if (messagesContainerRef?.current) {
            embed?.setEmbedState((prevState) => ({
                ...prevState,
                chatBacklogRef: messagesContainerRef,
            }))
        }
    }, [messagesContainerRef?.current])

    useEffect(() => {
        if (isChatOpen) {
            setFirstChatOpening(true)
        } else if (!isChatOpen && !activeVideoChannelId) {
            setFirstChatOpening(false)
        }
    }, [isChatOpen, activeVideoChannelId])

    useEffect(() => {
        if (isActiveVideoChannel && isEmbedQueueChannel) {
            if (isChatOpen) {
                const sendWrapper = sendContainerRef.current
                if (sendWrapper) {
                    const textarea = sendContainerRef.current?.querySelector('[data-id="send-message-textarea"]') as HTMLTextAreaElement
                    textarea?.focus()
                }
            } else {
                const iframe = document.querySelector('[data-id="jitsi-iframe-wrapper"] iframe') as HTMLIFrameElement
                if (iframe) {
                    iframe.focus()
                }
            }
        } else if (!isActiveVideoChannel && isEmbedQueueChannel) {
            const textarea = sendContainerRef.current?.querySelector('[data-id="send-message-textarea"]') as HTMLTextAreaElement
            textarea?.focus()
        }
    }, [isChatOpen, isActiveVideoChannel, isEmbedQueueChannel])

    useEffect(() => {
        setScrollToBottomDisplay({
            display: 'hide',
            isHighlighting: false,
            onClick: () => null,
            type: 'noop',
        })
    }, [chat])

    useEffect(() => {
        closeLightbox()

        return () => {
            closeLightbox()
        }
    }, [chat.channel_id])

    useEffect(() => {
        if (isVideoChannel) {
            // hide channel sidebar when switching to a video channel
            dispatch(updateShowChannelSidebar(false))
        }
    }, [chat.channel_id ?? chat.user_id])

    useEffect(() => {
        if (embed) {
            sendToParent({
                active: isJitsiActive,
                isVideoChannel,
                event: EmbedEvent.EmbedVideo,
                targetOrigin,
            })
        }
    }, [isJitsiActive, isVideoChannel])

    useEffect(() => {
        if (isClosed && isEmbedQueueChannel) {
            sendToParent({
                event: EmbedEvent.EmbedSound,
                sound: 'audienceEnded',
                targetOrigin,
            })
        }
    }, [isClosed, isEmbedQueueChannel])

    return (
        <StyledChat>
            <StyledChannel>
                {!isEmbedQueueChannel &&
                    <Menubar
                        chatProps={{
                            chat,
                            isActiveVideoChannel,
                            isAudienceChannel,
                            isChannelSidebarOpen: showChannelSidebar,
                            isChatOpen,
                            onToggleChannelSidebar,
                        }}
                    />}
                <StyledChannelContent>
                    {!!isVideoChannel && !isActiveVideoChannel && !isEmbedQueueChannel &&
                    <StyledVideoJoinDialogueContainer>
                        <StyledVideoJoinDialogue>
                            <VideoCameraIcon />
                            {!isJitsiActive &&
                                <h3>{t('This is a video meeting channel')}</h3>}
                            {!!isJitsiActive && !isActiveVideoChannel &&
                                <h3>{t('You have another video channel open')}</h3>}
                            <Button
                                color='filled'
                                disabled={isSuspended || isClosed}
                                onPress={() => onJoinVideoMeeting()}
                                text={t('Join video meeting')}
                                title={t('Join video meeting')}
                            />
                            {!!topic &&
                                <StyledTopic>
                                    <Highlight>{topic}</Highlight>
                                </StyledTopic>}
                        </StyledVideoJoinDialogue>
                    </StyledVideoJoinDialogueContainer>}
                    <StyledChannelMain
                        activeVideoChannel={isActiveVideoChannel}
                        channelSidebarOpen={showChannelSidebar}
                        chatOpen={isChatOpen}
                        css={
                            {$$containerHeaderHeight: isContainerWithHeader ? `${embed?.containerOptions?.headerOptions?.height || DEFAULT_HEADER_HEIGHT}px` : '0px'}
                        }
                        firstChatOpening={firstChatOpening}
                        isEmbedQueueChannel={isEmbedQueueChannel}
                        videoChannel={!!(isVideoChannel && !isEmbedQueueChannel)}
                    >
                        <StyledChannelMainContent
                            isEmbedQueueChannel={isEmbedQueueChannel}
                            videoChannel={!!(isVideoChannel && !isEmbedQueueChannel)}
                        >
                            {!!historyLoadStatus &&
                                <div className='historyLoader'>
                                    <div className='historyLoaderBar' />
                                </div>}
                            <StyledBacklogContainer
                                data-id='chat-backlog-container'
                                heroFullVisible={heroFullVisible}
                                isEmbedQueueChannel={isEmbedQueueChannel}
                                isVideoChannel={isVideoChannel}
                            >
                                {isActiveVideoChannel && isEmbedQueueChannel ?
                                    <StyledChatOnVideoHeader>
                                        <div>
                                            {t('Chat')}
                                        </div>
                                        <Button
                                            color='text'
                                            iconLeft={XIcon}
                                            onPress={handleCloseChatOnVideo}
                                            text=''
                                        />
                                    </StyledChatOnVideoHeader>
                                    : null}
                                <StyledBacklog data-id='chat-backlog' isEmbedQueueChannel={isEmbedQueueChannel} ref={messagesContainerRef}>
                                    {!!isAudienceChannel && !isEmbedQueueChannel &&
                                        <AudienceMetadata
                                            agentIds={agentIds}
                                            channelId={chat.channel_id as string}
                                            exclude={['ninchat', 'postAnswers', 'sso', 'device', 'vars']}
                                            openModal={(userId, sso, secure) => onOpenModal(userId, sso, secure)}
                                            realmId={realm_id}
                                        />}
                                    <Messages
                                        chat={chat}
                                        isAudienceChannel={isAudienceChannel}
                                        isNarrow={isVideoChannel}
                                        key={chat.channel_id ?? chat.user_id}
                                        messagesContainerRef={messagesContainerRef}
                                        queueId={queue_id}
                                        setHistoryLoadStatus={setHistoryLoadStatus}
                                        setScrollToBottomView={setScrollToBottomDisplay}
                                    />
                                    {isClosed && isEmbedQueueChannel ?
                                        <StyledCloseChatButtonContainer>
                                            <Button
                                                color='filled'
                                                onPress={() => embed?.closeEmbedRef?.current?.()}
                                                text={embed?.conversationEndedButtonText ?? t('Give rating and close')}
                                                title={embed?.conversationEndedButtonText ?? t('Give rating and close')}
                                            />
                                        </StyledCloseChatButtonContainer>
                                        : null}
                                </StyledBacklog>
                                <ScrollToBottom
                                    display={scrollToBottomDisplay.display}
                                    isHighlighting={scrollToBottomDisplay.isHighlighting}
                                    onClick={scrollToBottomDisplay.onClick} // eslint-disable-line react/jsx-handler-names
                                    type={scrollToBottomDisplay.type}
                                />
                            </StyledBacklogContainer>
                            <StyledCommandBarContainer
                                ref={sendContainerRef}
                            >
                                <Send
                                    cannedMessages={cannedMessages}
                                    chat={chat}
                                    closed={isClosed}
                                    features={features}
                                    isAudienceChannel={isAudienceChannel}
                                    isVideoChannel={isVideoChannel}
                                    realm_id={realm_id}
                                    ref={sendRef}
                                    supportedCannedMessagesLanguages={supportedCannedMessagesLanguages}
                                    suspended={isSuspended}
                                />
                            </StyledCommandBarContainer>
                        </StyledChannelMainContent>
                    </StyledChannelMain>
                </StyledChannelContent>
            </StyledChannel>
            <ChannelSidebar
                channelTopic={topic}
                chat={chat}
                hasCannedMessages={cannedMessages?.length > 0}
                isAudienceChannel={isAudienceChannel}
                onSelectCannedMessage={onSelectCannedMessage}
                onToggleChannelSidebar={onToggleChannelSidebar}
            />
        </StyledChat>
    )
}

export default ChatView
